import React from 'react'
import { scaleLinear } from 'd3-scale'
import { line } from 'd3-shape'

import find from 'lodash/find'

import { guff } from 'bam-node-baseball-savant/utils/guff'
import setTextColorWithBackground from 'bam-node-baseball-savant/utils/set-text-color-with-background.ts'
import zones, { zonesReversed } from '../../Utilities/ZoneSizing.ts'
import RunCalculations, {
    Calculations,
} from '../../Utilities/RunCalculations.ts'

import ColorPalette from '../../Utilities/Colors/Palettes.ts'
import { EVPData, ChartOptions, Settings } from '../../schema/types.ts'

// import ZoneLegend from "../Legend/ZoneLegend";

const zoneValues = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '11',
    '12',
    '13',
    '14',
]

const nullValues = [null, undefined, ''] as const

function isNullish(value: unknown): value is null | undefined | '' {
    return nullValues.includes(value as null | undefined | '')
}

const nullishColor = 'rgb(237, 237, 237)'

type ColorFn =
    | d3.ScaleLinear<string, number>
    | d3.ScaleSequential<string, never>
    | (() => string)

function getFill(
    chartType: keyof Calculations,
    calculations: Calculations,
    colorFn: ColorFn
) {
    const stat = calculations[chartType]

    if (isNullish(stat)) {
        return nullishColor
    }

    const color = colorFn(Number(stat))

    return calculations?.totalPitches ? color : nullishColor
}

const guffType = {
    attackAngle: 'attack_angle',
    attackDirection: 'attack_direction',
    batSpeed: 'sweetspot_speed_mph',
    batSpeedCompetitive: 'sweetspot_speed_mph',
    idealAttackAngle: 'rate_ideal_attack_angle',
    swingLength: 'swing_length',
    swingLengthCompetitive: 'swing_length',
    swingPathTilt: 'bat_path_angle',
}

type ZoneProps = {
    chartOptions: ChartOptions
    settings: Settings
}
export default function Zone({ settings, chartOptions }: ZoneProps) {
    // TODO: Can we improve this typing
    const chartType = chartOptions.type as keyof Calculations

    const pitcherView = chartOptions.pitcherPerspective

    let groupedData: Partial<Record<PropertyKey, EVPData[]>>
    // Use this option if you want to directly send in pre-processed zones
    if (!chartOptions.skipDataProcess) {
        groupedData = Object.groupBy(settings.data, (d) => d.zone || '')
    } else {
        groupedData = settings.data as unknown as Partial<
            Record<PropertyKey, EVPData[]>
        >
    }

    const counts = zoneValues.map((zone) => {
        if (groupedData[zone]) {
            return {
                zone: `zone_${zone}`,
                calculations: RunCalculations(
                    groupedData[zone],
                    settings.data.length
                ),
            }
        }
        return {
            zone: `zone_${zone}`,
            calculations: RunCalculations([], settings.data.length),
        }
    })

    const palette = new ColorPalette({
        type: chartType,
        data: counts,
    })

    const { color } = palette

    const x = scaleLinear().range([0, settings.vizWidth]).domain([-4, 4])

    const y = scaleLinear().range([settings.vizHeight, 0]).domain([-4, 4])

    const draw = line<{ x: number; y: number }>()
        .x((d) => x(d.x))
        .y((d) => y(d.y))

    function getValue(zoneNum: string) {
        const zoneObj = counts.find((d) => d.zone === zoneNum)

        if (!zoneObj?.calculations.totalPitches) {
            return '—'
        }

        try {
            const stat = zoneObj.calculations[chartType]
            if (isNullish(stat)) {
                return '—'
            }

            if (chartType in guffType) {
                return guff(stat, guffType[chartType as keyof typeof guffType])
            }

            return stat
        } catch (e) {
            console.error(e)
            return '—'
        }
    }

    const zonesView = pitcherView ? zonesReversed : zones

    const zonePaths = Object.entries(zonesView).map(([zone, points]) => {
        const id = Number(zone.split('_')[1])

        let dx = 0
        if (id < 10) {
            dx = x(1 / 3) - x(0)
        } else if (pitcherView && (id === 11 || id === 13)) {
            dx = -8
        } else if (!pitcherView && (id === 12 || id === 14)) {
            dx = -8
        } else if (pitcherView && (id === 12 || id === 14)) {
            dx = 8
        } else if (!pitcherView && (id === 11 || id === 13)) {
            dx = 8
        }

        let dy
        if (id < 10) {
            dy = y(0) - y(1 / 3)
        } else if (id === 11 || id === 12) {
            dy = 10
        } else {
            dy = -8
        }

        const count = find(counts, (d) => d.zone === zone)
        const classes = [
            `zone-text-${id > 10 ? 'outer' : 'inner'}`,
            `zone-text-${id}`,
            `zone-text-perspective-${pitcherView ? 'pitcher' : 'catcher'}`,
        ]

        const fill = count?.calculations
            ? getFill(chartType, count.calculations, color)
            : nullishColor

        const fillColor = count?.calculations?.totalPitches
            ? setTextColorWithBackground(String(fill))
            : '#666'

        return (
            <g key={`zone_${zone}`}>
                <path
                    id={zone}
                    d={draw(points) || undefined}
                    fill={fill !== undefined ? String(fill) : '#fff'}
                    className="evp-zone"
                    opacity={chartOptions.opacity}
                />
                {chartOptions.showText ? (
                    <text
                        className={classes.join(' ')}
                        x={x(points[0].x)}
                        y={y(points[0].y)}
                        fill={fillColor}
                        dx={dx}
                        dy={dy}
                    >
                        {getValue(zone)}
                    </text>
                ) : null}
            </g>
        )
    })

    return (
        <>
            <g id="zones">{zonePaths}</g>
            {/* <ZoneLegend
        settings={settings}
        range={[0, maxCount / 4, maxCount / 2, maxCount / 1.4, maxCount]}
        color={color}
      /> */}
        </>
    )
}
